import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiClient } from '../api'
import type {
  RosterAssignment,
  RosterAssignmentCreate,
  RosterAssignmentPatch,
  RosterResponse
} from '../types'

// Per-event Roster query + cell-write mutations (rebuild — migs
// 039/040/042; reads from PR R1, writes from PR R2).
//
// Returns { roles, days, assignments }. Mutations update the cached
// envelope's `assignments` array in place rather than invalidating
// the whole query — so only the cells that actually changed re-render.

export function useRoster(eventId: number) {
  return useQuery({
    queryKey: ['roster', eventId],
    queryFn: () => ApiClient.roster(eventId),
    enabled: Number.isFinite(eventId) && eventId > 0
  })
}

// ---- create ---------------------------------------------------------------

export function useCreateRosterAssignment(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: RosterAssignmentCreate) =>
      ApiClient.createRosterAssignment(eventId, body),
    onSuccess: (created: RosterAssignment) => {
      // Append into the cached envelope. The grid's per-cell
      // assignmentsByCell memo derives from `data.assignments`, so a
      // single append re-renders only the cell where the new chip lands.
      qc.setQueryData<RosterResponse>(['roster', eventId], (old) => {
        if (!old) return old
        return { ...old, assignments: [...old.assignments, created] }
      })
      // ScheduleTab derives crew per service from operational role
      // assignments on the event's start_date. Invalidate so the
      // schedule list refetches on next visit.
      qc.invalidateQueries({ queryKey: ['services', eventId] })
    }
  })
}

// ---- patch ----------------------------------------------------------------

export function usePatchRosterAssignment(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: RosterAssignmentPatch }) =>
      ApiClient.patchRosterAssignment(id, body),
    onSuccess: (updated: RosterAssignment) => {
      qc.setQueryData<RosterResponse>(['roster', eventId], (old) => {
        if (!old) return old
        return {
          ...old,
          assignments: old.assignments.map((a) =>
            a.id === updated.id ? updated : a
          )
        }
      })
      qc.invalidateQueries({ queryKey: ['services', eventId] })
    }
  })
}

// ---- delete ---------------------------------------------------------------

export function useDeleteRosterAssignment(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      ApiClient.deleteRosterAssignment(id).then(() => id),
    onSuccess: (id: number) => {
      qc.setQueryData<RosterResponse>(['roster', eventId], (old) => {
        if (!old) return old
        return {
          ...old,
          assignments: old.assignments.filter((a) => a.id !== id)
        }
      })
      qc.invalidateQueries({ queryKey: ['services', eventId] })
    }
  })
}
